import * as React from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppTheme from '../shared-theme/AppTheme';
import ColorModeIconDropdown from '../shared-theme/ColorModeIconDropdown';
import Copyright from '../dashboard/internals/components/Copyright';
import SignInDropdown from './SignInDropdown';
import { RefreshTokenManager } from '../utils/refreshTokenManager';

/** Base names for carousel screenshots (each has *Light.png and *Dark.png in public/img). */
const CAROUSEL_SCREENSHOTS = [
  'home-electricity-',
  'home-rates-',
  'home-histogram-',
  'home-tariff-'
];
const CAROUSEL_INTERVAL_MS = 8000;

/** Feature comparison: feature label, available on Free, available on Pro */
const FEATURE_COMPARISON: { feature: string; free: boolean; pro: boolean }[] = [
  { feature: 'Electricity & gas consumption dashboard', free: true, pro: true },
  { feature: 'Carbon emissions dashboard', free: true, pro: true },
  { feature: 'Upcoming tariff rates and carbon intensity', free: true, pro: true },
  { feature: 'Tariff comparison & insights', free: true, pro: true },
  { feature: '3 months of hour-by-hour smart-meter history', free: true, pro: true },
  { feature: 'Your full smart meter history at 30-minute resolution', free: false, pro: true },
  { feature: 'Advanced time of day consumption analysis', free: false, pro: true },
  { feature: 'Further advanced visuals and analyses', free: false, pro: true },
  { feature: 'Support development of the platform', free: false, pro: true },
];

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const NavButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: 'auto',
  px: 2,
  py: 1,
  borderRadius: '8px',
  '&.MuiButton-text, &.MuiButton-outlined': {
    color: theme.palette.text.primary,
  },
  '&.MuiButton-text:hover, &.MuiButton-outlined:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CtaButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '1.125rem',
  fontWeight: 600,
  textTransform: 'none',
  px: 2.5,
  py: 1.5,
  borderRadius: '10px',
}));

const COLOR_SCHEME_ATTR = 'data-mui-color-scheme';

/** Returns the applied color scheme from the document (same source that drives the theme). */
function getAppliedColorScheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  const value = document.documentElement.getAttribute(COLOR_SCHEME_ATTR);
  return value === 'dark' ? 'dark' : 'light';
}

function subscribeToColorScheme(callback: () => void): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: [COLOR_SCHEME_ATTR] });
  return () => observer.disconnect();
}

/** Hook that reads the actual applied color scheme from the document and re-renders when it changes. */
function useAppliedColorScheme(): 'light' | 'dark' {
  return React.useSyncExternalStore(subscribeToColorScheme, getAppliedColorScheme, getAppliedColorScheme);
}

/** Returns /img/{baseName}Light.png or Dark.png based on current theme */
function getThemedImagePath(baseName: string, mode: 'light' | 'dark') {
  return `/img/${baseName}${mode}.png`;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const mode = useAppliedColorScheme();
  const [authState, setAuthState] = React.useState<'unknown' | 'guest' | 'authenticated'>('unknown');

  React.useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token && !RefreshTokenManager.hasRefreshToken()) {
        if (!cancelled) {
          setAuthState('guest');
        }
        return;
      }

      const isAuthenticated = await RefreshTokenManager.ensureValidToken();
      if (!cancelled) {
        setAuthState(isAuthenticated ? 'authenticated' : 'guest');
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const n = CAROUSEL_SCREENSHOTS.length;
  const carouselSlides = n > 0 ? [...CAROUSEL_SCREENSHOTS, CAROUSEL_SCREENSHOTS[0]] : [];
  const [displayIndex, setDisplayIndex] = React.useState(0);
  const [transitionDisabled, setTransitionDisabled] = React.useState(false);
  const pendingPrevFromFirst = React.useRef(false);

  const logicalIndex = displayIndex >= n ? 0 : displayIndex;

  React.useEffect(() => {
    if (!transitionDisabled) return;
    const id = requestAnimationFrame(() => setTransitionDisabled(false));
    return () => cancelAnimationFrame(id);
  }, [transitionDisabled]);

  React.useEffect(() => {
    if (!pendingPrevFromFirst.current || displayIndex !== n) return;
    pendingPrevFromFirst.current = false;
    const id = requestAnimationFrame(() => {
      setTransitionDisabled(false);
      requestAnimationFrame(() => setDisplayIndex(n - 1));
    });
    return () => cancelAnimationFrame(id);
  }, [displayIndex, n]);

  const handleTransitionEnd = () => {
    if (displayIndex === n) {
      setTransitionDisabled(true);
      setDisplayIndex(0);
    }
  };

  React.useEffect(() => {
    const id = setInterval(() => {
      setDisplayIndex((i) => {
        if (i === n) {
          setTransitionDisabled(true);
          return 0;
        }
        return i + 1;
      });
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [n]);

  const goToPrev = () => {
    if (displayIndex === 0) {
      setTransitionDisabled(true);
      setDisplayIndex(n);
      pendingPrevFromFirst.current = true;
    } else {
      setDisplayIndex((i) => i - 1);
    }
  };
  const goToNext = () => {
    if (displayIndex === n) {
      setTransitionDisabled(true);
      setDisplayIndex(0);
    } else {
      setDisplayIndex((i) => i + 1);
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      {authState === 'authenticated' ? (
        <Navigate to="/app" replace />
      ) : authState === 'unknown' ? null : (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <StyledAppBar position="static">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
              px: { xs: 2, sm: 3 },
              minHeight: 64,
              maxWidth: 1200,
              width: '100%',
              mx: 'auto',
            }}
          >
            <Typography
              component={RouterLink}
              to="/"
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontVariant: 'small-caps',
                letterSpacing: '0.02em',
              }}
            >
              EnergyInfo.uk
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <ColorModeIconDropdown />
              <SignInDropdown
                renderTrigger={({ onClick }) => (
                  <NavButton onClick={onClick}>Sign in</NavButton>
                )}
              />
              <NavButton
                onClick={() => navigate('/register')}
                variant="contained"
                color="primary"
              >
                Register
              </NavButton>
            </Stack>
          </Box>
        </StyledAppBar>

        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            backgroundImage:
              theme.palette.mode === 'light'
                ? 'url(/light-bg.jpg)'
                : 'url(/dark-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          })}
        >
          <Box
            sx={{
              maxWidth: 1200,
              width: '100%',
              mx: 'auto',
              px: { xs: 2, sm: 3 },
            }}
          >
            <Stack spacing={4} alignItems="flex-start" textAlign="left" sx={{ py: { xs: 4, md: 6 } }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 500,
                  lineHeight: 1.3,
                  maxWidth: '40ch',
                }}
              >
                Explore your home electricity and gas usage.<br />
                Understand you energy costs and carbon emissions.
              </Typography>
              <CtaButton
                onClick={() => navigate('/register')}
                variant="contained"
                color="primary"
                size="large"
                sx={{ width: 'fit-content' }}
              >
                Start exploring
              </CtaButton>
            </Stack>
            <Box
              sx={(theme) => ({
                border: '1px solid',
                ml: -2,
                pl: 2,
                mr: -2,
                pr: 2,
                borderColor: 'divider',
                backgroundColor: theme.palette.surface,
                py: 4,
              })}
            >
              <Box sx={{ position: 'relative', width: '100%', px: { xs: 5, sm: 7 } }}>
                <Box sx={{ overflow: 'hidden', width: '100%' }}>
                <Box
                  onTransitionEnd={handleTransitionEnd}
                  sx={{
                    display: 'flex',
                    width: `${carouselSlides.length * 100}%`,
                    transform: `translateX(-${displayIndex * (100 / carouselSlides.length)}%)`,
                    transition: transitionDisabled ? 'none' : 'transform 0.4s ease-in-out',
                  }}
                >
                  {carouselSlides.map((baseName, i) => (
                    <Box
                      key={`${baseName}-${i}`}
                      sx={{
                        flex: `0 0 ${100 / carouselSlides.length}%`,
                        width: `${100 / carouselSlides.length}%`,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box
                        component="img"
                        src={getThemedImagePath(baseName, mode)}
                        alt={`Screenshot ${(i % n) + 1} of the Explore Your Energy service`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        sx={{
                          width: '100%',
                          display: 'block',
                          borderRadius: 2,
                          boxShadow: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
              {CAROUSEL_SCREENSHOTS.length > 1 && (
                <>
                  <Box
                    sx={(theme) => ({
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: theme.palette.surface,
                      borderRadius: 1,
                      boxShadow: 2,
                      '&:hover': {
                        backgroundColor: theme.palette.surface,
                      },
                    })}
                  >
                    <IconButton
                      onClick={goToPrev}
                      aria-label="Previous screenshot"
                      sx={{ color: 'text.primary' }}
                    >
                      <ChevronLeftIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={(theme) => ({
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: theme.palette.surface,
                      borderRadius: 1,
                      boxShadow: 2,
                      '&:hover': {
                        backgroundColor: theme.palette.surface,
                      },
                    })}
                  >
                    <IconButton
                      onClick={goToNext}
                      aria-label="Next screenshot"
                      sx={{ color: 'text.primary' }}
                    >
                      <ChevronRightIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={0.5}
                    sx={{ mt: 1.5 }}
                  >
                    {CAROUSEL_SCREENSHOTS.map((_, i) => (
                      <Box
                        key={i}
                        onClick={() => setDisplayIndex(i)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Go to screenshot ${i + 1}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDisplayIndex(i);
                          }
                        }}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: i === logicalIndex ? 'primary.main' : 'action.selected',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': { bgcolor: i === logicalIndex ? 'primary.dark' : 'action.hover' },
                        }}
                      />
                    ))}
                  </Stack>
                </>
              )}
              </Box>
            </Box>
          
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 500,
                lineHeight: 1.3,
                maxWidth: '40ch',
                pt: 4,
                pb: 2,
              }}
            >
              Take control of your energy.
            </Typography>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 4,
                border: '1px solid',
                borderColor: 'divider',
                mb: 4,
                ml: -2,
                pl: 2,
                mr: -2,
                pr: 2,
                pt: 2,
                pb: 2,
                backgroundColor: theme.palette.surface,
              })}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" component="h2" gutterBottom fontWeight={600}>
                  Track usage and spending
                </Typography>
                <Typography variant="body1">
                  See your electricity and gas consumption in one dashboard. Compare
                  tariffs, spot trends, and understand where your energy goes—so you
                  can cut waste and save money.
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 200,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={getThemedImagePath('home-electricity-', mode)}
                  alt="Usage and spending dashboard"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row-reverse' },
                alignItems: 'center',
                gap: 4,
                border: '1px solid',
                borderColor: 'divider',
                mb: 4,
                ml: -2,
                pl: 2,
                mr: -2,
                pr: 2,
                pt: 2,
                pb: 2,
                backgroundColor: theme.palette.surface,
              })}
            >

              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" component="h2" gutterBottom fontWeight={600}>
                  Smarter energy decisions
                </Typography>
                <Typography variant="body1">
                  Get clear insights and recommendations based on your actual usage.
                  Whether you want to lower bills, reduce carbon, or switch suppliers,
                  we help you make informed choices.
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 200,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={getThemedImagePath('home-rates-', mode)}
                  alt="Energy insights and recommendations"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            </Box>        
          </Box>

          <Container maxWidth="md" sx={{ py: 6 }}>
            <Typography variant="h2" component="h2" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Get started for free - no payment details required.
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom fontWeight={500} sx={{ mb: 2, color: 'text.primary' }}>
              A free account gives you access to all the information you need to start understanding your energy usage. Upgrade to a Pro account to access your full smart meter data and unlock advanced visualisations and analytics.
            </Typography>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table size="small" aria-label="Feature comparison" sx={{ '& .MuiTableCell-root': { py: 0.75 } }}>
                <TableHead>
                  <TableRow sx={(theme) => ({ bgcolor: theme.palette.background.default })}>
                    <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Free</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Pro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FEATURE_COMPARISON.map((row) => (
                    <TableRow
                      key={row.feature}
                      sx={(theme) => ({ bgcolor: theme.palette.background.paper })}
                    >
                      <TableCell><Typography component="span" fontWeight={500}>{row.feature}</Typography></TableCell>
                      <TableCell align="center">
                        {row.free ? <CheckIcon color="primary" fontSize="large" /> : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.pro ? <CheckIcon color="primary" fontSize="large" /> : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <CtaButton
                onClick={() => navigate('/register')}
                variant="contained"
                color="primary"
                size="large"
                sx={{ width: 'fit-content' }}
              >
                Sign up now
              </CtaButton>
            </Box>
          </Container>
        </Box>

        <Box
          component="footer"
          sx={{
            py: 1.5,
            px: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Copyright sx={{ mt: 0, mb: 0 }} />
        </Box>
      </Box>
      )}
    </AppTheme>
  );
}
